import { logger } from '@/logging';
import { Configuration, LogLevel } from '@azure/msal-browser';

/**
 * Check if auth bypass is enabled for local development
 */
export const isAuthBypassed = import.meta.env.VITE_AUTH_BYPASS === 'true';

/**
 * Validates that required environment variables are present
 */
function validateEnvironment(): void {
  // Skip validation if auth is bypassed
  if (isAuthBypassed) {
    logger.info('Auth bypass enabled - skipping Azure AD configuration validation');
    return;
  }

  const requiredVars = [
    'VITE_AZURE_CLIENT_ID',
    'VITE_AZURE_TENANT_ID',
  ];

  const missing = requiredVars.filter(varName => !import.meta.env[varName]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(', ')}. ` +
        'Please check your .env file.'
    );
  }
}

// Validate on module load
validateEnvironment();

/**
 * MSAL configuration for Azure AD authentication
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            // eslint-disable-next-line no-console
            console.info(message);
            return;
          case LogLevel.Verbose:
            // eslint-disable-next-line no-console
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Verbose : LogLevel.Warning,
    },
  },
};

/**
 * Scopes requested during login
 * Using OpenID Connect scopes for user authentication
 * Since we're using ID tokens for SSO validation, we don't need API scopes
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * Scopes for acquiring tokens silently
 */
export const tokenRequest = {
  scopes: ['openid', 'profile', 'email'],
  forceRefresh: false, // Let MSAL handle token refresh intelligently
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs
 * Using basic scopes since API validates based on SSO + email allowlist
 */
export const protectedResources = {
  apiEndpoint: {
    endpoint: import.meta.env.VITE_API_BASE_URL,
    scopes: ['openid', 'profile', 'email'],
  },
};
